import type { MovementVector } from '../input/input-contract';

export const PILOT_SPEED_PER_SECOND = 240;
export const MAX_SIMULATION_DELTA_MS = 50;

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface ArenaBounds {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

const PILOT_VISUAL_BOUNDS = { left: 28, right: 28, top: 42, bottom: 28 } as const;

export const clampPilotToArena = (pilot: Point, arena: ArenaBounds): Point => ({
  x: Math.min(
    arena.right - PILOT_VISUAL_BOUNDS.right,
    Math.max(arena.left + PILOT_VISUAL_BOUNDS.left, pilot.x),
  ),
  y: Math.min(
    arena.bottom - PILOT_VISUAL_BOUNDS.bottom,
    Math.max(arena.top + PILOT_VISUAL_BOUNDS.top, pilot.y),
  ),
});

export interface PilotSimulationState {
  readonly pilot: Point;
  readonly elapsedMilliseconds: number;
  readonly hazardTimeMilliseconds: number;
  readonly score: number;
}

export const createPilotSimulation = (pilot: Point): PilotSimulationState => ({
  pilot: { ...pilot },
  elapsedMilliseconds: 0,
  hazardTimeMilliseconds: 0,
  score: 0,
});

const normalizeMovement = ({ x, y }: MovementVector): MovementVector => {
  const magnitude = Math.hypot(x, y);
  if (magnitude === 0) return { x: 0, y: 0 };
  if (magnitude <= 1) return { x, y };
  return { x: x / magnitude, y: y / magnitude };
};

export const stepPilotSimulation = (
  state: PilotSimulationState,
  movement: MovementVector,
  deltaMilliseconds: number,
  paused: boolean,
): PilotSimulationState => {
  if (paused || !Number.isFinite(deltaMilliseconds) || deltaMilliseconds <= 0) return state;

  const delta = Math.min(deltaMilliseconds, MAX_SIMULATION_DELTA_MS);
  const normalized = normalizeMovement(movement);
  const distance = PILOT_SPEED_PER_SECOND * (delta / 1_000);
  const isMoving = normalized.x !== 0 || normalized.y !== 0;

  return {
    pilot: {
      x: state.pilot.x + normalized.x * distance,
      y: state.pilot.y + normalized.y * distance,
    },
    elapsedMilliseconds: state.elapsedMilliseconds + delta,
    hazardTimeMilliseconds: state.hazardTimeMilliseconds + delta,
    score: state.score + (isMoving ? delta : 0),
  };
};
